import React, { Suspense, useMemo } from 'react';
import { MobileNavigation } from 'shared/components/connected/mobile-navigation';
import { SideNavigation } from 'shared/components/connected/side-navigation';
import { PageLoader } from 'shared/components/ui/page-loader';
import { useActivePath } from 'shared/hooks/use-active-path';
import { useToggle } from 'shared/hooks/use-toggle';

import { NavigationContextProps, SidePanelsContext } from './context';
import * as Styled from './side-panels.style';
import { useKeyboardShortcuts } from './use-keyboard-shortcuts';
import { useToggleSideNav } from './use-toggle-side-nav';

type NavigationProviderProps = {
  children: React.ReactNode;
};

export const NavigationProvider: React.FC<NavigationProviderProps> = ({
  children,
}) => {
  const [
    mobileSideNavigationOpen,
    toggleMobileSideNavigation,
    forceMobileSideNavigation,
  ] = useToggle(false);
  const { open: sideNavigationOpen, toggle: toggleSideNavigation } =
    useToggleSideNav();
  const activePath = useActivePath({ level: 2 });
  useKeyboardShortcuts();

  const onNavItem = () => forceMobileSideNavigation(false);

  const value = useMemo<NavigationContextProps>(
    () => ({
      sideNavigationOpen,
      toggleSideNavigation,
      mobileSideNavigationOpen,
      toggleMobileSideNavigation,
    }),
    [
      mobileSideNavigationOpen,
      sideNavigationOpen,
      toggleMobileSideNavigation,
      toggleSideNavigation,
    ],
  );

  return (
    <SidePanelsContext.Provider value={value}>
      <Styled.Container>
        <SideNavigation
          open={sideNavigationOpen}
          toggleOpen={toggleSideNavigation}
          mobileOpen={mobileSideNavigationOpen}
          toggleMobileOpen={toggleMobileSideNavigation}
          onNavigationItem={onNavItem}
          activePath={activePath}
        />

        <Styled.Body>
          <Suspense fallback={<PageLoader />}>
            <Styled.Content>{children}</Styled.Content>
          </Suspense>

          <MobileNavigation />
        </Styled.Body>
      </Styled.Container>
    </SidePanelsContext.Provider>
  );
};
